import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { FaEye } from 'react-icons/fa6';
import { BeatLoader } from 'react-spinners';
import Pagination from '../Pagination';
import Title from '../Title';
import { formatDate } from '../../utils/Date';
import {
  fetchReports,
  fetchReportById,
  deleteReport,
  updateBusinessStatus,
} from '../../services/BusinessManagement/BusinessReportService';
import { showConfirmToast, showSuccess, showError } from '../../components/Ui/ToastNotification';
import Modal from '../Ui/Modal';
import { CiPower } from 'react-icons/ci';

const BusinessReport = () => {
  const [activePopoverId, setActivePopoverId] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { currentPage, entriesPerPage } = useSelector(state => state.pagination);
  const searchQuery = useSelector(state => state.search.query);

  useEffect(() => {
    let isMounted = true;

    const loadReports = async () => {
      setLoading(true);
      try {
        const response = await fetchReports(currentPage, entriesPerPage);
        if (isMounted) {
          setReports(Array.isArray(response.reports) ? response.reports : []);
          showSuccess('Reports loaded successfully');
        }
      } catch (error) {
        console.error('Error loading reports:', error);
        showError('Failed to load reports');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReports();

    return () => {
      isMounted = false;
    };
  }, [currentPage, entriesPerPage]);

  const handleDelete = async reportId => {
    showConfirmToast({
      message: 'Are you sure you want to delete this report? This action cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await deleteReport(reportId);
          setReports(prev => prev.filter(report => report._id !== reportId));
          showSuccess('Report deleted successfully');
        } catch (error) {
          showError(error.response?.data?.message || 'Failed to delete report');
        }
      },
    });
  };

  const handleStatusChange = async (businessId, status) => {
    showConfirmToast({
      message: `Are you sure you want to ${
        status === 'disable' ? 'disable' : 'activate'
      } this business?`,
      confirmText: status === 'disable' ? 'Disable' : 'Activate',
      onConfirm: async () => {
        try {
          await updateBusinessStatus(businessId, status);
          showSuccess(`Business ${status === 'disable' ? 'disabled' : 'activated'} successfully`);
          const response = await fetchReports(currentPage, entriesPerPage);
          setReports(Array.isArray(response.data.reports) ? response.data.reports : []);
        } catch (error) {
          showError(error.response?.data?.message || `Failed to update business status`);
        }
      },
    });
  };

  const handleViewReport = async reportId => {
    try {
      setLoading(true);
      const response = await fetchReportById(reportId);

      const reportData = response.data || response;

      setSelectedReport(reportData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading report details:', error);
      showError('Failed to load report details');
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = reports.filter(report => {
    return searchQuery
      ? [report.title, report.description].some(field =>
          field?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : true;
  });

  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const togglePopover = id => {
    setActivePopoverId(prev => (prev === id ? null : id));
  };

  const EmptyReportsState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-lg">
      <div className="mb-6 text-gray-400">
        <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
          />
        </svg>
      </div>
      <h3 className="mb-3 text-lg font-light text-gray-600">
        {searchQuery ? 'No matching reports found' : 'No reports yet'}
      </h3>
      <p className="max-w-md mb-6 text-sm font-light text-gray-500">
        {searchQuery
          ? 'No reports match your search criteria. Try adjusting your search terms.'
          : 'When users report businesses for review, the reports will appear here for management.'}
      </p>
      {!searchQuery && (
        <div className="space-y-2">
          <p className="text-xs font-light text-gray-400">
            Reports will appear here once submitted by users
          </p>
        </div>
      )}
    </div>
  );

  const renderReportRow = report => (
    <tr
      key={report?._id}
      onClick={() => handleViewReport(report?._id)}
      className="border-b font-nohemi font-light text-sm text-[#4A5154] hover:bg-[#F0F6FF] cursor-pointer">
      <td className="flex items-center px-3 pt-4 pb-2 space-x-1">
        <span>{report?.userId?.lastName}</span>
      </td>
      <td className="p-2">{report?.title}</td>
      <td className="flex items-center px-3 pt-4 pb-2 space-x-1">
        <img
          src={report?.businessId?.media[0]?.fileUrl}
          alt={report?.businessId?.name}
          className="w-6 h-6 rounded-full"
        />
        <span>{report?.businessId?.name}</span>
      </td>
      <td className="p-2 truncate max-w-[200px]">{report?.description}</td>
      <td className="p-2">{formatDate(report?.createdAt)}</td>
      <td className="relative w-8 h-8">
        <button
          className="focus:ring-0 focus:outline-none mx-2 w-[80%] h-[50%] rounded-full"
          onClick={() => togglePopover(report?._id)}>
          <BsThreeDotsVertical className="text-[#191B1C]" title="More options" />
        </button>
        {activePopoverId === report?._id && (
          <div
            onClick={e => e.stopPropagation()}
            className="absolute z-10 mt-1 text-sm text-gray-500 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-md top-full left-1/2 w-max">
            <div className="flex flex-col p-2 space-y-1">
              <button
                className="inline-flex items-center text-[10px] text-gray-700"
                onClick={e => {
                  e.stopPropagation();
                  handleViewReport(report?._id);
                }}>
                <FaEye className="m-1" /> View Details
              </button>
              <button
                className="inline-flex items-center text-[10px] text-red-600"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(report?._id);
                }}>
                <RiDeleteBin5Line className="m-1" /> Delete
              </button>
              {report?.businessId?.status === 'active' ? (
                <button
                  className="inline-flex items-center text-[10px] text-orange-600"
                  onClick={e => {
                    e.stopPropagation();
                    handleStatusChange(report.businessId, 'disable');
                  }}>
                  <CiPower className="m-1" /> Disable Business
                </button>
              ) : (
                <button
                  className="inline-flex items-center text-[10px] text-green-600"
                  onClick={e => {
                    e.stopPropagation();
                    handleStatusChange(report?.businessId, 'active');
                  }}>
                  <CiPower className="m-1" /> Activate Business
                </button>
              )}
            </div>
          </div>
        )}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div
        data-testid="beat-loader"
        className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="">
        <div className="flex items-center mb-6">
          <Title title="Business Reports" subtitle="Business Management &gt; Reports" />
        </div>
        <div className="relative overflow-x-auto bg-[#f8f9fa] rounded-lg">
          {filteredReports.length === 0 ? (
            <EmptyReportsState />
          ) : (
            <div className="w-full p-6 bg-white rounded-t-2xl">
              <div className="w-full pt-2 bg-[#F7F7F7] rounded-t-2xl">
                <table className="w-full mx-auto text-left">
                  <thead>
                    <tr className="font-nohemi text-sm font-semibold text-[#808587] bg-[#F5F6F7]">
                      <td className="p-3">Reported By</td>
                      <td className="p-3">Title</td>
                      <td className="p-3">Reported Business</td>
                      <td className="p-3">Description</td>
                      <td className="p-3">Date Reported</td>
                      <td className="p-3">Actions</td>
                    </tr>
                  </thead>
                  <tbody>{paginatedReports.map(renderReportRow)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {filteredReports.length > entriesPerPage && (
          <Pagination totalEntries={filteredReports.length} />
        )}

        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          {selectedReport && (
            <div className="p-4">
              <h2 className="mb-4 text-xl font-bold">{selectedReport?.title}</h2>

              <div className="mb-4">
                <h3 className="font-semibold">Description:</h3>
                <p className="text-gray-700">{selectedReport?.description}</p>
              </div>

              {selectedReport.imageUrl && (
                <div className="mb-4">
                  <h3 className="font-semibold">Attachment:</h3>
                  <img
                    src={selectedReport?.imageUrl}
                    alt="Report attachment"
                    className="h-auto max-w-full rounded"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="font-semibold">Reported Business:</h3>
                  <div className="flex items-center mt-1 space-x-2">
                    {selectedReport?.businessId?.media &&
                      selectedReport?.businessId?.media[0]?.fileUrl && (
                        <img
                          src={selectedReport?.businessId?.media[0]?.fileUrl}
                          alt={selectedReport?.businessId?.name || 'Business'}
                          className="w-8 h-8 rounded-full"
                        />
                      )}
                    <p className="text-gray-700">
                      {selectedReport?.businessId?.name || 'Unknown Business'}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold">Reported by:</h3>
                  <div className="flex items-center mt-1 space-x-2">
                    {selectedReport?.userId?.image_url && (
                      <img
                        src={selectedReport?.userId?.image_url}
                        alt={selectedReport?.userId?.lastName || 'User'}
                        className="w-8 h-8 rounded-full"
                      />
                    )}
                    <p className="text-gray-700">
                      {selectedReport?.userId?.lastName || 'Unknown User'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <h3 className="font-semibold">Date Reported:</h3>
                <p className="text-gray-700">{formatDate(selectedReport?.createdAt)}</p>
              </div>

              <div className="flex justify-end mt-4 space-x-2">
                {selectedReport?.businessId.status === 'active' ? (
                  <button
                    onClick={() => {
                      handleStatusChange(
                        selectedReport?.businessId._id || selectedReport?.businessId,
                        'disable'
                      );
                      setIsModalOpen(false);
                    }}
                    className="px-4 py-2 text-white bg-red-500 rounded hover:bg-red-600">
                    Disable Business
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      handleStatusChange(
                        selectedReport?.businessId._id || selectedReport?.businessId,
                        'active'
                      );
                      setIsModalOpen(false);
                    }}
                    className="px-4 py-2 text-white bg-green-500 rounded hover:bg-green-600">
                    Activate Business
                  </button>
                )}
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-white bg-gray-500 rounded hover:bg-gray-600">
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default BusinessReport;
